import React from 'react';
import { chartColor } from '../chartTheme';
import { formatPercent } from '../../../../lib/campaign-analytics/format';

/** One segment of a 100% stacked bar. */
export interface StackSegment {
  key: string;
  label: string;
  count: number;
  /** Token reference for the segment colour. */
  color: string;
}

/** One 100% stacked bar. */
export interface StackRow {
  key: string;
  /** Row label; omitted for a single bar. */
  label?: string;
  segments: StackSegment[];
}

/** Props for {@link StackedBar}. */
export interface StackedBarProps {
  rows: StackRow[];
  onSelect?: (row: StackRow, segment: StackSegment) => void;
}

/** Segments narrower than this share print no inside label (the table and hover carry it). */
const LABEL_MIN_SHARE = 0.08;

/**
 * 100% stacked bars on a common baseline, segments in scale order. A single
 * key row maps colours to categories (the one legend allowed: a colour scale).
 */
export const StackedBar: React.FC<StackedBarProps> = ({ rows, onSelect }) => {
  const keys = rows[0]?.segments ?? [];
  return (
    <div data-chart="stacked" className="flex flex-col gap-2">
      <div data-legend className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {keys.map(segment => (
          <span key={segment.key} className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm" style={{ background: segment.color }} />
            {segment.label}
          </span>
        ))}
      </div>
      {rows.map(row => {
        const total = row.segments.reduce((sum, segment) => sum + segment.count, 0);
        return (
          <div key={row.key} className="flex flex-col gap-1">
            {row.label && <span className="truncate text-xs text-muted-foreground" data-tip={row.label} data-tip-truncated>{row.label}</span>}
            <div className="flex h-6 w-full overflow-hidden rounded-sm">
              {row.segments.filter(segment => segment.count > 0).map(segment => {
                const share = total ? segment.count / total : 0;
                const label = `${row.label ? `${row.label} · ` : ''}${segment.label}`;
                const detail = `${segment.count} · ${formatPercent(share)}`;
                const name = `${label}: ${detail}`;
                const className = 'flex h-full items-center justify-center border-r-2 border-background text-[11px] font-medium tabular-nums last:border-r-0 focus-visible:outline-2 focus-visible:outline-ring';
                const style = { width: `${share * 100}%`, background: segment.color, color: chartColor.onMark };
                const text = share >= LABEL_MIN_SHARE ? formatPercent(share) : '';
                return onSelect ? (
                  <button key={segment.key} type="button" aria-label={name} data-tip={label} data-tip-detail={detail} data-mark className={`${className} cursor-pointer`} style={style} onClick={() => onSelect(row, segment)}>
                    {text}
                  </button>
                ) : (
                  <span key={segment.key} role="img" aria-label={name} data-tip={label} data-tip-detail={detail} tabIndex={0} data-mark className={className} style={style}>{text}</span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Detractor, passive and promoter colours for NPS. */
export const NPS_COLORS = { detractors: chartColor.negative, passives: chartColor.categorical[5], promoters: chartColor.positive } as const;
